import { FC, useEffect } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import {
  MapLibreWalkingRoute,
  resolveStationPinAppearance,
  useMapLibreHoverPopup,
} from "@ryde/controls";
import {
  getIsValidStationLocation,
  IPassengerStationsMapInfo,
  IWarkDistanceAndTime,
  passengerMapIsPickupStation,
  passengersMapAddStationSearchValueAtom,
  passengersMapCurrentPolylineViewAtom,
  passengersMapLastAddedStationIdAtom,
  passengersStationsMapDataAtom,
} from "@ryde/shared";

import {
  usePassengerMapSelectedStations,
  usePassengerMapStations,
} from "../../hooks";
import { PassengersMapMarker } from "./passengers-map-marker";
import { PassengersMapSearchPin } from "./passengers-map-search-pin";

export const PassengersMapMarkers: FC = () => {
  const [
    passengersMapCurrentPolylineView,
    setPassengersMapCurrentPolylineView,
  ] = useRecoilState(passengersMapCurrentPolylineViewAtom);

  const passengersStationsMapData = useRecoilValue(
    passengersStationsMapDataAtom,
  );
  const passengersMapAddStationSearchValue = useRecoilValue(
    passengersMapAddStationSearchValueAtom,
  );
  const passengersMapLastAddedStationId = useRecoilValue(
    passengersMapLastAddedStationIdAtom,
  );

  const { openId, open, close, scheduleClose, cancelScheduledClose } =
    useMapLibreHoverPopup();

  const {
    distanceAndTimeCacheRefByIds,
    getAddNewStationsWalkDistanceAndTime,
    getAssignStationsWalkDistanceAndTime,
  } = usePassengerMapStations();

  const { passengersMapSelectedStationDictionary, handleStationClick } =
    usePassengerMapSelectedStations();

  // useEvent, not useCallback: its deps change with the selection, and a new identity there would
  // re-render every marker on the map.
  const onHoverMarker = useEvent(
    async (stationMapData: IPassengerStationsMapInfo) => {
      cancelScheduledClose();
      const isSelected = passengersMapSelectedStationDictionary?.has(
        stationMapData.stationInfo.id,
      );

      if (isSelected) {
        open(stationMapData.stationInfo.id);
      }

      let res: (void | IWarkDistanceAndTime)[] = [];

      const { latitude: originLat, longitude: originLng } =
        passengersMapAddStationSearchValue ?? {};

      if (originLat && originLng) {
        res = await getAddNewStationsWalkDistanceAndTime(
          { originLat, originLng },
          stationMapData.stationInfo,
        );
      } else {
        res = await getAssignStationsWalkDistanceAndTime(stationMapData);
      }

      if (res) {
        setPassengersMapCurrentPolylineView(res.filter(r => !!r));
      }
    },
  );

  // A popup only ever belongs to a selected station, so it has to go the moment that station is
  // deselected. Watching the selection rather than the click covers every route to it — clicking the pin
  // to toggle it off, clearing from a panel, or the grid selection changing underneath.
  useEffect(() => {
    if (
      openId !== null &&
      !passengersMapSelectedStationDictionary?.has(openId)
    ) {
      close(openId);
    }
  }, [openId, passengersMapSelectedStationDictionary, close]);

  const onLeaveMarker = useEvent(
    (stationMapData: IPassengerStationsMapInfo) => {
      const isSelected = passengersMapSelectedStationDictionary?.has(
        stationMapData.stationInfo.id,
      );

      if (isSelected) {
        scheduleClose(stationMapData.stationInfo.id);
      }
    },
  );

  return (
    <>
      {(passengersStationsMapData ?? []).map(stationMapData => {
        const { stationInfo } = stationMapData ?? {};

        if (!getIsValidStationLocation(stationInfo)) {
          return null;
        }

        const isSelected = Boolean(
          passengersMapSelectedStationDictionary?.has(stationInfo.id),
        );

        const pin = resolveStationPinAppearance({
          isFlagVariant: passengerMapIsPickupStation(stationInfo),
          isSelected,
          isHighlighted: passengersMapLastAddedStationId === stationInfo.id,
        });

        return (
          <PassengersMapMarker
            key={stationInfo.id}
            stationMapData={stationMapData}
            pinType={pin.type}
            pinColor={pin.color}
            isPopupOpen={openId === stationInfo.id}
            distanceAndTimeCacheRefByIds={distanceAndTimeCacheRefByIds}
            onClick={handleStationClick}
            onHover={onHoverMarker}
            onLeave={onLeaveMarker}
            onPopupEnter={open}
            onPopupLeave={scheduleClose}
            onPopupClose={close}
          />
        );
      })}

      {passengersMapCurrentPolylineView.map((walk, index) => (
        <MapLibreWalkingRoute
          key={index}
          id={`passengers-map-walk-${index}`}
          positions={walk.path as unknown as [number, number][]}
          distanceAndTime={walk}
        />
      ))}

      {getIsValidStationLocation(passengersMapAddStationSearchValue) && (
        <PassengersMapSearchPin location={passengersMapAddStationSearchValue} />
      )}
    </>
  );
};
